
import React from 'react';
import { useDispatch, useSelector  } from 'react-redux';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';
import GeoJSON from 'ol/format/GeoJSON';

import {
    DialogConfirm,
    IfComp,
    ToolbarCommandButton
} from '../../../core';

import {
    sagas as sagasMap,
    getTempFeature
} from '../../../map';

import {
    getDataProjectId,
    getMapLayerById,
    useToken
} from '../../../project';

import { getActions } from '../../sagas';

import { getInfo } from '../../reducers'
import {
    INFO_TYPE_OBJECT,
} from '../../reducers/infoPanelType';
import {
    getObjectEdit,
    getObjectEditKey,
    getObjectEditChanged
} from '../../reducers/info'

import validateAttributeValue from '../validateAttributeValue';
import useDataModelItem from '../../hooks/useDataModelItem';
import { useDataModelOperationUpdate } from '../../hooks/useDataModelOperation';

import ObjectEditor from './ObjectEditor';
import ObjectValidationResult from '../ObjectValidationResult';

const ObjectEditPanel = ({appActions}) => {

    const classNames = mergeStyleSets({
        ObjectEditPanel: {
        },
        content: {
            overflowY: 'auto',
            height: 0
        }
    });

    const dispatch = useDispatch();
    const dataActions = getActions(dispatch);
    const mapActions = sagasMap.getActions(dispatch);

    const dataProjectId = useSelector(state => getDataProjectId(state.project));

    const objectEdit = useSelector(state => getObjectEdit(getInfo(state.data)));
    const objectEditKey = useSelector(state => getObjectEditKey(getInfo(state.data)));
    const objectEditChanged = useSelector(state => getObjectEditChanged(getInfo(state.data)));
    const project = useSelector(state => state.project);
    const mapLayer = getMapLayerById(project, objectEdit.layerId);
    const dataModelItem = useDataModelItem(mapLayer);
    const tempFeature = useSelector(state => getTempFeature(state.map.map));
    const operationUpdate = useDataModelOperationUpdate(dataModelItem);
    const token = useToken();

    React.useEffect(() => {
        if (dataModelItem && (operationUpdate && (operationUpdate.allowGeoModify !== false))) {
            let format = new GeoJSON();
            let feature = format.readFeature(objectEdit.attributes._geom);
            mapActions.addCommand('SET_TEMP_FEATURE', {features: [feature]});
            mapActions.setToolsMode('MODIFY_FEATURE');
        } else {
            handleHighlightOn();
        }

        return () => {
            if (operationUpdate && (operationUpdate.allowGeoModify !== false)) {
                mapActions.setToolsMode('DEFAULT');
                mapActions.addCommand('SET_TEMP_FEATURE', {features: []});
                mapActions.setTempFeature();
            } else {
                handleHighlightOff();
            }
        };

    }, [objectEditKey]);

    React.useEffect(() => {
        if (tempFeature) {
            let format = new GeoJSON();
            let geoJson = format.writeGeometry(tempFeature.getGeometry());
            dataActions.setObjectEditAttrValue('_geom', geoJson);
        }
    }, [tempFeature]);

    const handleHighlightOn = () => {
        mapActions.addCommand(
            'HIGHLIGHT_FEATURE',
            {
                geoJson: objectEdit.attributes._geom
            }
        );
    }

    const handleHighlightOff = () => {
        mapActions.addCommand(
            'HIGHLIGHT_FEATURE', {geoJsons: []}
        );
    }

    const handleBackClick = async () => {
        const canBack = (objectEditChanged.length > 0)
            ? await DialogConfirm.show(`Opravdu chcete odejít zpět na detail objektu bez uložení změn ?`)
            : true;
        if (canBack) {
            dataActions.setType(INFO_TYPE_OBJECT);
        }
    }

    const handleChangeEditor = (attrName, value) => {

        const attribute = dataModelItem.attributes.reduce((previous, current) => {
            return (current.name === attrName)
                ? current
                : previous;
        }, null);

        if (attribute && ((attribute.type === 'float') || (attribute.type === 'integer')) && value === '') {
            value = null;
        }

        dataActions.setObjectEditAttrValue(attrName, value);
    }

    const handleSaveClick = () => {
        const attrNames = [
            ...objectEditChanged,
            'gid'
        ];

        dataActions.updateObject(
            token, dataProjectId, dataModelItem.id, objectEdit.layerId,
            {
                attributes: Object.keys(objectEdit.attributes).reduce((previous, current) => {
                    if (attrNames.indexOf(current) !== -1) {
                        previous[current] = objectEdit.attributes[current];
                    }
                    return previous;
                }, {})
            }
        );
    }

    const handleValidate = () => {
        let errors = [];
        dataModelItem.attributes.forEach(item => {
            if (objectEditChanged.indexOf(item.name) !== -1) {
                if (!validateAttributeValue(item, objectEdit.attributes[item.name])) {
                    errors.push(`Atribut "${item.title}" má špatně zadanou hodnotu`);
                }
            }
        });
        return errors;
    }

    let commands = [];

    commands.push({
        text: 'Uložit',
        title: 'Uložit nové hodnoty',
        disabled: (
            (objectEditChanged.length === 0) ||
            (handleValidate().length > 0)
        ),
        onClick: handleSaveClick
    });
    commands.push({
        text: 'Zpět',
        title: 'Zpět na detail objektu bez uložení změn',
        onClick: handleBackClick
    })

    return (
        <Stack verticalFill className={classNames.ObjectEditPanel} tokens={{childrenGap: 8}}>
            <IfComp cond={dataModelItem}>
                <Stack.Item grow className={classNames.content}>
                    <ObjectEditor
                        dataModel={dataModelItem}
                        object={objectEdit}
                        onChange={handleChangeEditor}
                        mode="update"
                    />
                </Stack.Item>
            </IfComp>
            <Stack.Item>
                <ObjectValidationResult
                    items={handleValidate()}
                    detailed={true}
                    showValidText={false}
                />
            </Stack.Item>
            <ToolbarCommandButton
                items={commands}
            />
        </Stack>
    );
}

export default ObjectEditPanel;
